/**
 * Browser detection utilities for handling restricted browser contexts
 * (WebViews, Chrome Custom Tabs) that have isolated storage
 */

/**
 * Detect if running in a WebView or Chrome Custom Tab
 * These contexts have isolated storage and may cause auth issues
 */
export const isRestrictedBrowserContext = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  
  const ua = navigator.userAgent;
  
  // Android WebView detection
  // WebViews typically include 'wv' or have 'Version/' pattern with Android
  const isAndroidWebView = ua.includes('wv') || 
    (ua.includes('Android') && ua.includes('Version/'));
  
  // iOS WebView detection
  // iOS WebViews have AppleWebKit but NOT Safari in the user agent
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua);
  
  // Chrome Custom Tab detection
  // CCT doesn't have a reliable UA indicator, but we can detect common patterns:
  // - Opened from Gmail (referrer check)
  // - No window.opener (opened as a new context)
  // - Common email app referrers
  const referrer = document.referrer.toLowerCase();
  const isFromEmailApp = !window.opener && (
    referrer.includes('mail.google.com') ||
    referrer.includes('gmail') ||
    referrer.includes('outlook') ||
    referrer.includes('yahoo.com/mail')
  );
    
  return isAndroidWebView || isIOSWebView || isFromEmailApp;
};

/**
 * Detect if running in any kind of in-app browser
 * More aggressive detection for scenarios where we want to be extra cautious
 */
export const isInAppBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent.toLowerCase();
  
  // Common in-app browser patterns
  const inAppPatterns = [
    'fbav',           // Facebook App
    'fban',           // Facebook App
    'fb_iab',         // Facebook In-App Browser
    'instagram',      // Instagram
    'twitter',        // Twitter/X
    'line/',          // Line
    'wechat',         // WeChat
    'micromessenger', // WeChat
    'linkedinapp',    // LinkedIn
    'snapchat',       // Snapchat
    'tiktok',         // TikTok
  ];
  
  return inAppPatterns.some(pattern => ua.includes(pattern)) || isRestrictedBrowserContext();
};
